'use client'

import Image from 'next/image'
import { ImageCard } from './ImageCard'
import { Loader2, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { StoredImage } from '@/types/image'

interface UploadingFile {
  id: string
  name: string
  preview: string
  status:
    | 'queued'
    | 'initializing'
    | 'uploading'
    | 'finalizing'
    | 'success'
    | 'failed'
    | 'aborted'
  progress: number
  error?: string
  isHeic?: boolean
}

interface ImageGridProps {
  /** Array of images to display */
  images: StoredImage[]
  /** Currently selected image ID (for "Use Selected") */
  selectedId: string | null
  /** Set of multi-selected image IDs (for bulk operations) */
  selectedIds?: Set<string>
  /** Called when an image is selected for use */
  onSelect: (image: StoredImage) => void
  /** Called when multi-select is toggled on an image - receives image object for duplicate support */
  onMultiSelect?: (imageId: string, image: StoredImage) => void
  /** Called when edit is clicked on an image */
  onEdit: (image: StoredImage) => void
  /** Called when favorite is toggled on an image */
  onToggleFavorite?: (imageId: string) => void
  /** IDs of images that should show "NEW" badge */
  newImageIds?: Set<string>
  /** Show empty state with upload prompt */
  showEmptyState?: boolean
  /** Called when upload button clicked in empty state */
  onUploadClick?: () => void
  /** Files currently being uploaded (show placeholder cards) */
  uploadingFiles?: UploadingFile[]
  onCancelUpload?: (fileId: string) => void
  onRetryUpload?: (fileId: string) => void
  /** Called to remove a canceled/failed upload card from the list */
  onRemoveUpload?: (fileId: string) => void
  /** Map of image ID to selection count (for showing quantity badges) */
  selectionCountMap?: Map<string, number>
  /** When set, overrides Tailwind responsive grid with an explicit column count */
  columns?: number
  /** Gap in px between cells — used when columns is set */
  gap?: number
  /** Passed to next/image sizes for correct CDN hinting */
  imageSizes?: string
}

/** Matches upload throttle (~150ms) so progress bar feels responsive. */
const PROGRESS_BAR_TRANSITION = 'duration-150 ease-out'

export function ImageGrid({
  images,
  selectedId,
  selectedIds = new Set(),
  onSelect,
  onMultiSelect,
  onEdit,
  onToggleFavorite,
  newImageIds = new Set(),
  showEmptyState = true,
  onUploadClick,
  uploadingFiles = [],
  onCancelUpload,
  onRetryUpload,
  onRemoveUpload,
  selectionCountMap,
  columns,
  gap = 8,
  imageSizes,
}: ImageGridProps) {
  const getUploadStatusLabel = (file: UploadingFile): string => {
    if (file.status === 'uploading') return `Uploading ${file.progress}%`
    if (file.status === 'initializing' && file.isHeic) return 'Converting…'
    if (file.status === 'initializing') return 'Preparing upload'
    if (file.status === 'finalizing') return 'Finalizing'
    if (file.status === 'queued') return 'Queued'
    if (file.status === 'failed') return 'Upload failed'
    if (file.status === 'aborted') return 'Upload canceled'
    return 'Uploaded'
  }

  const UploadCard = onUploadClick ? (
    <button
      onClick={onUploadClick}
      className={cn(
        'aspect-square rounded-lg overflow-hidden',
        'border-2 border-dashed border-border',
        'bg-muted/30 hover:bg-muted/50 hover:border-primary/50',
        'flex flex-col items-center justify-center gap-1',
        'transition-all duration-200 cursor-pointer',
        'touch-manipulation group'
      )}
    >
      <div
        className={cn(
          'p-2 rounded-full bg-muted group-hover:bg-primary/10',
          'transition-colors duration-200'
        )}
      >
        <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
      <span className="text-[10px] sm:text-xs text-muted-foreground group-hover:text-foreground transition-colors">
        Upload
      </span>
    </button>
  ) : null

  const UploadingCard = ({ file }: { file: UploadingFile }) => {
    const isPreparing =
      file.status === 'initializing' || file.status === 'queued'
    return (
      <div
        className={cn(
          'group relative aspect-square rounded-xl overflow-hidden',
          'border border-border/70 bg-card/70 backdrop-blur-sm shadow-sm',
          'touch-pan-y',
          isPreparing && 'animate-pulse'
        )}
      >
        {file.preview ? (
          <Image
            src={file.preview}
            alt={`Uploading ${file.name}`}
            fill
            className="object-cover opacity-45 saturate-[0.9]"
            sizes="(max-width: 640px) 33vw, (max-width: 768px) 25vw, (max-width: 1024px) 20vw, 12.5vw"
          />
        ) : (
          <div className="absolute inset-0 bg-muted" aria-hidden />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/35 to-black/70 p-2.5 flex flex-col justify-end pointer-events-none">
          <div className="text-[10px] text-white font-semibold truncate drop-shadow-sm">
            {file.name}
          </div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <div className="text-[10px] text-white/95">
              {getUploadStatusLabel(file)}
            </div>
            {(file.status === 'initializing' ||
              file.status === 'queued' ||
              file.status === 'uploading' ||
              file.status === 'finalizing') && (
              <Loader2 className="w-3 h-3 text-white/90 animate-spin" />
            )}
          </div>
          <div className="mt-1.5 h-1.5 w-full rounded-full bg-white/25 overflow-hidden">
            <div
              className={cn(
                'h-full transition-all',
                PROGRESS_BAR_TRANSITION,
                file.status === 'failed' || file.status === 'aborted'
                  ? 'bg-rose-300'
                  : file.status === 'success'
                    ? 'bg-emerald-300'
                    : 'bg-white/95'
              )}
              style={{ width: `${Math.min(Math.max(file.progress, 0), 100)}%` }}
            />
          </div>
          {file.error && (
            <div className="mt-1 text-[10px] text-rose-100/95 line-clamp-2">
              {file.error}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {file.status === 'failed' && onRetryUpload && (
              <button
                className="px-2 py-0.5 text-[10px] rounded-md bg-white/20 text-white hover:bg-white/30 transition-colors pointer-events-auto touch-manipulation"
                onClick={() => onRetryUpload(file.id)}
                type="button"
              >
                Retry
              </button>
            )}
            {file.status === 'aborted' && onRetryUpload && (
              <button
                className="px-2 py-0.5 text-[10px] rounded-md bg-white/20 text-white hover:bg-white/30 transition-colors pointer-events-auto touch-manipulation"
                onClick={() => onRetryUpload(file.id)}
                type="button"
              >
                Re-upload
              </button>
            )}
            {(file.status === 'queued' ||
              file.status === 'initializing' ||
              file.status === 'uploading' ||
              file.status === 'finalizing') &&
              onCancelUpload && (
                <button
                  className="px-2 py-0.5 text-[10px] rounded-md bg-white/20 text-white hover:bg-white/30 transition-colors pointer-events-auto touch-manipulation"
                  onClick={() => onCancelUpload(file.id)}
                  type="button"
                >
                  Cancel
                </button>
              )}
            {(file.status === 'failed' || file.status === 'aborted') &&
              onRemoveUpload && (
                <button
                  className="px-2 py-0.5 text-[10px] rounded-md bg-white/20 text-white hover:bg-white/30 transition-colors pointer-events-auto touch-manipulation"
                  onClick={() => onRemoveUpload(file.id)}
                  type="button"
                >
                  Remove
                </button>
              )}
          </div>
        </div>
      </div>
    )
  }

  if (images.length === 0 && uploadingFiles.length === 0 && showEmptyState) {
    return (
      <div
        style={
          columns
            ? {
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap: `${gap}px`,
              }
            : undefined
        }
        className={
          columns
            ? ''
            : cn(
                'grid gap-2 sm:gap-3',
                'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8'
              )
        }
      >
        {UploadCard}
      </div>
    )
  }

  return (
    <div
      style={
        columns
          ? {
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              gap: `${gap}px`,
            }
          : undefined
      }
      className={
        columns
          ? ''
          : cn(
              'grid gap-2 sm:gap-3',
              'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8'
            )
      }
    >
      {/* Upload card as first item */}
      {UploadCard}

      {/* Uploading placeholder cards */}
      {uploadingFiles.map((file) => (
        <UploadingCard key={file.id} file={file} />
      ))}

      {/* Image cards */}
      {images.map((image) => (
        <ImageCard
          key={image.id}
          image={image}
          isSelected={selectedId === image.id}
          isMultiSelected={selectedIds.has(image.id)}
          selectionCount={selectionCountMap?.get(image.id)}
          onSelect={() => onSelect(image)}
          onMultiSelect={
            onMultiSelect ? () => onMultiSelect(image.id, image) : undefined
          }
          onEdit={() => onEdit(image)}
          onToggleFavorite={
            onToggleFavorite ? () => onToggleFavorite(image.id) : undefined
          }
          isNew={newImageIds.has(image.id)}
          sizes={imageSizes}
        />
      ))}
    </div>
  )
}
